import { FC, useState, useContext, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { ThemeProvider } from 'styled-components';
import { Brand } from '@stax-core/types-omni';
import { Sidebar } from './components/layout/Sidebar/Sidebar';
import { Navbar } from './components/layout/Navbar';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { routes, redirects } from './routes/private';
import { RouteItem, RouteMulti } from './routes/RouteItem';
import { useWindowSize } from './hooks';
import { BrandStore, AuthStore, convertBrandToTheme, Auth } from './context';
import { InlineLogo } from './components/layout/Logo';
import { GlobalStyle } from './components/layout/GlobalStyle';
import { PrivateRoute } from './routes/PrivateRoute';
import { MerchantSwitcher } from './components/layout/MerchantSwitcher';
import { Tracker } from './components/tracker/Tracker';
import { AppLayout } from './components/layout/AppLayout';
import { StandaloneDocumentsPage } from './pages/documents/StandaloneDocumentsPage';
import { LiveChat } from './components/live-chat/LiveChat';
import { PlaidCallback } from './components/Plaid/PlaidCallback';
import { StandaloneDocumentsConfirmationPage } from './pages/documents/StandaloneDocumentsConfirmationPage';
import { FullPageLoader } from './components';

function createThemeWithBrand(
  theme: any,
  brand: Brand | null,
  brandName: string | null,
  auth: Auth | null,
) {
  // if there was a cached brandName and it matches the auth brand
  // just go ahead and set the theme now to speed things up
  if (!theme && auth && brand && brandName && brandName === auth?.brand) {
    return convertBrandToTheme(brand);
  }
  return null;
}

export const App: FC = () => {
  const { pathname } = useLocation();
  const { isMobile } = useWindowSize();
  const {
    state: { brand, brandName },
  } = useContext(BrandStore);
  const {
    state: { auth },
  } = useContext(AuthStore);
  const [theme, setTheme] = useState<any>(() =>
    createThemeWithBrand(null, brand, brandName, auth),
  );

  const [logo] = (brand && (brand.logoPngWideWhite || brand.logoPngWide)) || [];

  useEffect(() => {
    const themeRes = createThemeWithBrand(theme, brand, brandName, auth);
    if (themeRes) setTheme(themeRes);
  }, [auth, brand, brandName, theme]);

  if (!theme) {
    return <FullPageLoader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Tracker>
        <ReactTooltip />
        <ScrollToTop />
        <LiveChat />
        <GlobalStyle theme={theme} />
        <Switch>
          <PrivateRoute path="/callback">
            <AppLayout id="oauth-callback">
              <PlaidCallback />
            </AppLayout>
          </PrivateRoute>
          <PrivateRoute path="/documents/confirmation">
            <AppLayout id="documents-confirmation">
              <StandaloneDocumentsConfirmationPage />
            </AppLayout>
          </PrivateRoute>
          <PrivateRoute path="/documents">
            <AppLayout id="app">
              <StandaloneDocumentsPage />
            </AppLayout>
          </PrivateRoute>
          <Route path="/">
            <Sidebar logo={logo?.url}>
              <MerchantSwitcher />
            </Sidebar>
            <AppLayout id="app" data-with-sidebar={true}>
              <Navbar routes={routes}>
                {isMobile && pathname.includes('welcome') && (
                  <InlineLogo>
                    <img alt="logo" src={logo?.url} />
                  </InlineLogo>
                )}
              </Navbar>
              <Switch>
                {routes.map((route, key) => (
                  <PrivateRoute
                    key={key}
                    path={
                      (route as RouteItem).path || (route as RouteMulti).paths
                    }
                    component={route.component}
                  />
                ))}
                {redirects.map((redirect, key) => (
                  <Redirect key={key} from={redirect.from} to={redirect.to} />
                ))}
              </Switch>
            </AppLayout>
          </Route>
        </Switch>
      </Tracker>
    </ThemeProvider>
  );
};
